import { API_BASE_URL } from './client'

/**
 * Self-service sign-up flow — public, no-auth endpoints (the caller has no
 * MSAL token yet). Split out of `lib/api.ts` (WO-10.4). These are plain
 * exported functions (not part of the `api`/`tcmApi` objects) — that shape
 * is unchanged from before the split.
 */

export interface InitiateSignUpRequest {
  customerEmail: string
  customerName: string
  companyName: string
}

export interface InitiateSignUpResponse {
  success: boolean
  sessionId?: string
  consentUrl?: string
  error?: string
}

export interface CompleteSignUpRequest {
  tenantId: string
  state: string
  adminConsent: boolean
  error?: string
  errorDescription?: string
}

export interface CompleteSignUpResponse {
  success: boolean
  tenantId?: string
  tenantName?: string
  error?: string
  /**
   * The tenant already had a Guardian row, which the callback left unchanged
   * (SignUpCallbackResponse.AlreadySetUp). Not a new sign-up: the caller shows
   * the neutral already-connected page, not the consent-granted success page.
   */
  alreadySetUp?: boolean
}

export interface SignUpStatusResponse {
  sessionId: string
  status: string
  tenantId?: string
  tenantName?: string
  createdAt: string
  completedAt?: string
}

// Self-Service Sign-Up API functions (public, no auth required)
export async function initiateSignUp(data: InitiateSignUpRequest): Promise<InitiateSignUpResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/signup/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.error || `Request failed with status ${response.status}`,
      }
    }

    return response.json()
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error occurred',
    }
  }
}

export async function completeSignUp(data: CompleteSignUpRequest): Promise<CompleteSignUpResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/signup/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.error || errorData.errorMessage || `Request failed with status ${response.status}`,
      }
    }

    // A handled failure comes back as HTTP 200 with { success:false, errorMessage }
    // (SignUpCallbackResponse.ErrorMessage), e.g. consent still propagating. Map
    // errorMessage -> error so the callback UI shows that specific, actionable
    // message instead of the generic "we could not complete your sign-up" fallback.
    const body = await response.json()
    return {
      success: body.success,
      tenantId: body.tenantId,
      tenantName: body.tenantName,
      error: body.error ?? body.errorMessage,
      alreadySetUp: body.alreadySetUp === true,
    }
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error occurred',
    }
  }
}

export async function getSignUpStatus(sessionId: string): Promise<SignUpStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/signup/status/${sessionId}`)

  if (!response.ok) {
    throw new Error(`Failed to get status: ${response.status}`)
  }

  return response.json()
}
